import {AbstractService} from "@/services/AbstractService";
import {getJsonFromBlob, isJson} from "@/helpers";
import {AddToCart} from "@/services/observer/shopify/httpRequest/AddToCart";
import {AbstractHttpRequestObserver} from "@/services/observer/common/AbstractHttpRequestObserver";
import {app} from "@/app";

export class HttpRequests extends AbstractService {

    requests = [
        // shopify
        new AddToCart(),
    ];

    async init() {
        if(!app.configOption('shopify.observer.httpRequest.enabled', true)) {
            app.debug('Observer Requests: disabled');
            return;
        }

        await this.initXMLHttpRequest()
        await this.initFetch()
    }

    async initXMLHttpRequest() {
        const service = this;

        const XMLHttpRequestOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function onRequestOpen(method: string, url: string, async?: boolean | null, username?: string | null, password?: string | null): void {
            const request = this;

            if(!service.getApplicableRequests(method, url).length) {
                // @ts-ignore
                return XMLHttpRequestOpen.call(request, method, url, async, username, password);
            }

            request['algomaPixelTracking'] = true;

            app.debug('Observer Request: XMLHttpRequestOpen', {method, url});

            request.addEventListener("readystatechange", function onRequestOpen(event) {
                if (request.readyState === XMLHttpRequest.DONE) {
                    service.onXMLHttpRequestCompleted(request, method, url)
                    request.removeEventListener("readystatechange", onRequestOpen)
                }
            });
            // @ts-ignore
            return XMLHttpRequestOpen.call(request, method, url, async, username, password);
        };

        const XMLHttpRequestSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.send = function(body?: Document | XMLHttpRequestBodyInit | null): void {
            const request = this;
            if(!('algomaPixelTracking' in request) || !request['algomaPixelTracking']) {
                return XMLHttpRequestSend.call(request, body);
            }

            request['algomaRequestData'] = body ?? null;

            app.debug('Observer Request: XMLHttpRequestSend', body);
            return XMLHttpRequestSend.call(request, body)
        }
    }

    async initFetch() {
        const service = this;
        const originalFetch = window.fetch;
        window.fetch = async (input: RequestInfo, init?: RequestInit) => {
            const url = input.toString();
            const method = init?.method ?? 'GET';

            if(!service.getApplicableRequests(method, url).length) return originalFetch(input, init);

            app.debug('Observer Request: Fetch: Args:', {input, init});

            const response = await originalFetch(input, init);
            response
                .clone()
                .json()
                .then(body => {
                    app.debug('Observer Request: Fetch: Response:', body);
                    this.resolveResponse(method, url, 'fetch', body);
                })
                .catch(err => {
                    app.debug('Observer Request: Fetch: Error:', err, 'error');
                });

            return response;
        }
    }

    getApplicableRequests(method: string, url: string): AbstractHttpRequestObserver[] {
        return this.requests.filter(requestObserver => requestObserver.isApplicable(method, url));
    }

    onXMLHttpRequestCompleted(request: XMLHttpRequest, method: string, url: string)
    {
        if(200 !== request.status) return;

        let responseType = ('' == request.responseType)
            ? isJson(request.response)
                ? 'json'
                : null
            : request.responseType;

        let response: Record<any, any> | null = null;
        if('blob' === responseType) {
            getJsonFromBlob(request.response).then(response => this.resolveResponse(method, url, 'blob', response));
            return;
        }
        if('json' === responseType && 'string' === typeof request.response) {
            response = JSON.parse(request.response)
        }

       if(responseType && ['json', 'document'].indexOf(responseType) != -1) {
           this.resolveResponse(method, url, 'ajax', response);
       }
    }

    resolveResponse(method: string, url: string, responseSource: string, response: null|Record<any, any>)
    {
        const applicableRequests = this.getApplicableRequests(method, url);
        app.debug('Observer Request: resolveResponse', {method, url, responseSource, response, applicableRequestsLength: applicableRequests.length});
        applicableRequests.forEach((request: AbstractHttpRequestObserver) => {
            request.resolve(method, url, responseSource, response);
        });
    }
}
